import React from 'react'
import { motion } from 'framer-motion'
import { 
  Code, 
  Cloud, 
  Shield, 
  Users, 
  BarChart3, 
  Globe,
  Cpu,
  Network,
  TrendingUp
} from 'lucide-react'

const Expertise: React.FC = () => {
  const skillCategories = [
    {
      title: "Project & Delivery Management",
      skills: [
        { name: "Agile Project Management", icon: <TrendingUp className="w-6 h-6" />, level: 95 },
        { name: "Process Optimization", icon: <Users className="w-6 h-6" />, level: 90 },
        { name: "Project Budgeting & Cost Control", icon: <BarChart3 className="w-6 h-6" />, level: 92 }
      ]
    },
    {
      title: "Technical Oversight",
      skills: [
        { name: "Mobile App Development (iOS & Android)", icon: <Code className="w-6 h-6" />, level: 88 },
        { name: "AI & Blockchain Integration", icon: <Cpu className="w-6 h-6" />, level: 85 },
        { name: "Full-Stack Development", icon: <Network className="w-6 h-6" />, level: 90 }
      ]
    },
    {
      title: "Team & Operations Leadership",
      skills: [
        { name: "Cross-functional Team Leadership", icon: <Users className="w-6 h-6" />, level: 95 },
        { name: "DevOps Awareness", icon: <Cloud className="w-6 h-6" />, level: 87 },
        { name: "Performance Management", icon: <TrendingUp className="w-6 h-6" />, level: 90 }
      ]
    },
    {
      title: "Tools & Methodologies",
      skills: [
        { name: "Jira & Confluence", icon: <Shield className="w-6 h-6" />, level: 90 },
        { name: "MS Project", icon: <BarChart3 className="w-6 h-6" />, level: 85 },
        { name: "PRINCE2 & Scrum", icon: <Globe className="w-6 h-6" />, level: 92 }
      ]
    }
  ]

  const technologies = [
    { 
      name: "Swift", 
      logo: "/apple-logo.png",
      category: "iOS Development" 
    },
    { 
      name: "React Native", 
      logo: "/atom.png",
      category: "Cross-Platform" 
    },
    { 
      name: "Flutter", 
      logo: "/flag.png",
      category: "Cross-Platform" 
    },
    { 
      name: "Kotlin", 
      logo: "/android.png",
      category: "Android Development" 
    },
    { 
      name: "Node.js", 
      logo: "/nodejs.png",
      category: "Backend" 
    },
    { 
      name: "PHP", 
      logo: "/php.png",
      category: "Backend" 
    },
    { 
      name: ".NET", 
      logo: "/dnet.png",
      category: "Backend" 
    },
    { 
      name: "MySQL", 
      logo: "/mysql.png",
      category: "Database" 
    },
    { 
      name: "MERN Stack", 
      logo: "/mern.png",
      category: "Full-Stack" 
    },
    { 
      name: "AI/ML", 
      logo: "/ai.png",
      category: "Emerging Tech" 
    },
    { 
      name: "Blockchain", 
      logo: "/blockchain.png",
      category: "Emerging Tech" 
    },
    { 
      name: "DevOps", 
      logo: "/devops.png",
      category: "Infrastructure" 
    }
  ]

  return (
    <section id="expertise" className="section-padding bg-gray-50 dark:bg-gray-800/30">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Expertise & <span className="gradient-text">Skills</span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            A comprehensive skill set honed through 15+ years of software project management and operations leadership
          </p>
        </motion.div>

        {/* Skill Categories */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {skillCategories.map((category, categoryIndex) => (
            <motion.div
              key={category.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: categoryIndex * 0.1 }}
              viewport={{ once: true }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <h3 className="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-6">
                {category.title}
              </h3>
              <div className="space-y-4">
                {category.skills.map((skill, skillIndex) => (
                  <motion.div
                    key={skill.name}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: skillIndex * 0.1 }}
                    viewport={{ once: true }}
                    className="space-y-2"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="text-primary-500 dark:text-primary-400">
                          {skill.icon}
                        </div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          {skill.name}
                        </span>
                      </div>
                      <span className="text-sm font-semibold text-primary-600 dark:text-primary-400">
                        {skill.level}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${skill.level}%` }}
                        transition={{ duration: 1, delay: 0.5 + skillIndex * 0.1 }}
                        viewport={{ once: true }}
                        className="bg-gradient-to-r from-primary-500 to-secondary-500 h-2 rounded-full"
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Technology Stack */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h3 className="text-3xl font-bold text-gray-800 dark:text-gray-200 mb-8">
            Technology <span className="gradient-text">Stack</span>
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
            {technologies.map((tech, index) => (
              <motion.div
                key={tech.name}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.1, y: -5 }}
                className="group cursor-pointer"
              >
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-md hover:shadow-lg transition-all duration-300 border border-gray-200 dark:border-gray-700">
                  <div className="mb-3 group-hover:scale-110 transition-transform duration-300 flex justify-center">
                    <img 
                      src={tech.logo} 
                      alt={`${tech.name} logo`}
                      className="max-w-full max-h-16 w-auto h-auto object-contain"
                      style={{ maxHeight: '64px' }}
                    />
                  </div>
                  <h4 className="font-semibold text-gray-800 dark:text-gray-200 text-sm mb-1 text-center">
                    {tech.name}
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                    {tech.category}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Certifications & Achievements */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <h3 className="text-3xl font-bold text-gray-800 dark:text-gray-200 mb-8">
            Certifications & <span className="gradient-text">Achievements</span>
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Certified ScrumMaster (CSM)", level: "Scrum Alliance", year: "Current" },
              { title: "PRINCE2® 6th Edition Practitioner", level: "PeopleCert", year: "Current" },
              { title: "PRINCE2® 6th Edition Foundation", level: "PeopleCert", year: "Current" }
            ].map((cert, index) => (
              <motion.div
                key={cert.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">
                  {cert.title}
                </h4>
                <p className="text-primary-600 dark:text-primary-400 font-medium mb-1">
                  {cert.level}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {cert.year}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default Expertise
